import {
  AvgDeltaTime,
  Cn5gStatus,
  GnbStatus,
  MachineId,
  OaiboxStatus,
  OaiboxTelemetryDto,
  StompService,
  TenantId,
} from "@allbesmart/web-app-common";
import { HttpClient } from "@angular/common/http";
import { EventEmitter, Injectable, NgZone } from "@angular/core";
import { filter, first, Observable, Subscription } from "rxjs";
import { environment } from "../../environments/environment";
import { HTTP_ENDPOINTS } from "../external/http";
import { WS_SUFFIX } from "../external/ws";
import { SavedOaiboxTelemetryDto } from "../models/saved-oaibox-telemetry-dto";

@Injectable({
  providedIn: "root",
})
export class OaiboxTelemetryService {
  private savedTelemetries: Map<MachineId, SavedOaiboxTelemetryDto> = new Map();
  private savedTelemetriesSubscription?: Subscription;

  private oaiboxTelemetries: Map<MachineId, OaiboxTelemetryDto> = new Map();
  private oaiboxTelemetriesSubscriptions: Map<MachineId, Subscription> =
    new Map();
  private oaiboxTelemetriesStatusTimeout: Map<
    MachineId,
    ReturnType<typeof setTimeout>
  > = new Map();
  private avgTelemetriesDeltaTimesMs: Map<MachineId, AvgDeltaTime> = new Map();

  private oaiboxTelemetryChanges: EventEmitter<MachineId> = new EventEmitter();
  private oaiboxStatusChanges$: EventEmitter<void> = new EventEmitter();

  constructor(
    private stompService: StompService,
    private http: HttpClient,
    private ngZone: NgZone
  ) {
    this.loadLastSavedTelemetries();
  }

  private loadLastSavedTelemetries(): void {
    if (this.savedTelemetriesSubscription)
      this.savedTelemetriesSubscription.unsubscribe();
    this.savedTelemetriesSubscription = this.http
      .get<SavedOaiboxTelemetryDto[]>(
        `${environment.apiBasePath}/${HTTP_ENDPOINTS.FETCH_LATEST_TELEMETRY}`
      )
      .pipe(first())
      .subscribe((oaiboxSavedTelemetries) => {
        this.savedTelemetries = new Map(
          oaiboxSavedTelemetries.map((telemetry) => [
            telemetry.machineId,
            telemetry,
          ])
        );
        oaiboxSavedTelemetries
          .filter(
            (telemetry) => !this.oaiboxTelemetries.has(telemetry.machineId)
          )
          .forEach((telemetry) => {
            telemetry.status =
              Date.now() - telemetry.timestamp <
              environment.oaiboxTelemetryExpiryMs * 3
                ? OaiboxStatus.ONLINE
                : OaiboxStatus.OFFLINE;
          });
        this.oaiboxStatusChanges$.emit();
      });
  }

  public listenToOaiboxTelemetry(
    tenantId: TenantId,
    machineId: MachineId
  ): void {
    if (!this.oaiboxTelemetriesSubscriptions.has(machineId)) {
      this.oaiboxTelemetriesSubscriptions.set(
        machineId,
        this.stompService
          .subscribeTopic<OaiboxTelemetryDto>(
            `${tenantId}.${machineId}.${WS_SUFFIX.OAIBOX_TELEMETRY}`
          )
          .subscribe((freshOaiboxTelemetry) => {
            this.ngZone.runOutsideAngular(() => {
              freshOaiboxTelemetry.status = OaiboxStatus.ONLINE;

              const previousTelemetry = this.getOaiboxTelemetry(machineId);
              const previousStatus = previousTelemetry?.status;
              const previousCommitId = previousTelemetry?.commitId;

              let avgDeltaTime = this.avgTelemetriesDeltaTimesMs.get(machineId);
              if (!avgDeltaTime)
                this.avgTelemetriesDeltaTimesMs.set(
                  machineId,
                  (avgDeltaTime = new AvgDeltaTime(
                    environment.oaiboxTelemetryExpiryMs
                  ))
                );

              const oaiboxTelemetryOptional =
                this.oaiboxTelemetries.get(machineId);
              if (oaiboxTelemetryOptional) {
                // Update OAIBOX Telemetry
                clearTimeout(
                  this.oaiboxTelemetriesStatusTimeout.get(machineId)
                );
                this.updateOaibox(
                  oaiboxTelemetryOptional,
                  freshOaiboxTelemetry
                );
              } else {
                // New OAIBOX Telemetry
                this.oaiboxTelemetries.set(machineId, freshOaiboxTelemetry);
              }

              // Create offline timeout
              this.oaiboxTelemetriesStatusTimeout.set(
                machineId,
                setTimeout(
                  () => {
                    const oaiboxTelemetry =
                      this.oaiboxTelemetries.get(machineId);
                    if (oaiboxTelemetry) {
                      oaiboxTelemetry.status = OaiboxStatus.OFFLINE;
                      oaiboxTelemetry.cn5gStatus = Cn5gStatus.STOPPED;
                      oaiboxTelemetry.gnbStatus = GnbStatus.GNB_STOPPED;
                    }
                    this.oaiboxTelemetryChanges.emit(machineId);
                    this.oaiboxStatusChanges$.emit();
                  },
                  avgDeltaTime.getDeltaTime(freshOaiboxTelemetry.timestamp) * 3
                )
              );
              this.oaiboxTelemetryChanges.emit(machineId);

              const currentTelemetry = this.getOaiboxTelemetry(machineId);
              if (
                currentTelemetry?.status !== previousStatus ||
                currentTelemetry?.commitId !== previousCommitId
              ) {
                this.oaiboxStatusChanges$.emit();
              }
            });
          })
      );
    }
  }

  public stopListeningOaiboxTelemetry(machineId: MachineId): void {
    this.oaiboxTelemetriesSubscriptions.get(machineId)?.unsubscribe();
    this.oaiboxTelemetriesSubscriptions.delete(machineId);
    this.oaiboxTelemetries.delete(machineId);

    clearTimeout(this.oaiboxTelemetriesStatusTimeout.get(machineId));
    this.oaiboxTelemetriesStatusTimeout.delete(machineId);
  }

  private updateOaibox(
    oaiboxToUpdate: OaiboxTelemetryDto,
    freshOaiboxTelemetry: OaiboxTelemetryDto
  ): OaiboxTelemetryDto {
    oaiboxToUpdate.ip = freshOaiboxTelemetry.ip;
    oaiboxToUpdate.product = freshOaiboxTelemetry.product;
    oaiboxToUpdate.status = freshOaiboxTelemetry.status;
    oaiboxToUpdate.cn5gStatus = freshOaiboxTelemetry.cn5gStatus;
    oaiboxToUpdate.gnbStatus = freshOaiboxTelemetry.gnbStatus;
    oaiboxToUpdate.timestamp = freshOaiboxTelemetry.timestamp;
    oaiboxToUpdate.t2LdpcOffloadingAvailable =
      freshOaiboxTelemetry.t2LdpcOffloadingAvailable;
    oaiboxToUpdate.connectionQuality = freshOaiboxTelemetry.connectionQuality;
    return oaiboxToUpdate;
  }

  public getOaiboxTelemetry(
    machineId: MachineId
  ): OaiboxTelemetryDto | undefined {
    return (
      this.oaiboxTelemetries.get(machineId) ??
      this.savedTelemetries.get(machineId)
    );
  }

  public getOaiboxTelemetry$(
    machineId: MachineId
  ): Observable<OaiboxTelemetryDto> {
    return new Observable((subscriber) => {
      const oaiboxTelemetry = this.getOaiboxTelemetry(machineId);
      if (oaiboxTelemetry) subscriber.next(oaiboxTelemetry);

      const subscription = this.oaiboxTelemetryChanges
        .pipe(filter((changedMachineId) => changedMachineId === machineId))
        .subscribe((changedMachineId) => {
          const oaiboxTelemetryOptional =
            this.getOaiboxTelemetry(changedMachineId);
          if (oaiboxTelemetryOptional) subscriber.next(oaiboxTelemetryOptional);
        });
      return { unsubscribe: () => subscription.unsubscribe() };
    });
  }

  public getOaiboxStatusChanges$(): Observable<void> {
    return this.oaiboxStatusChanges$;
  }
}
